import { UA } from '../constants';

/**
 * 是否为微信或企业微信环境，且排除PC端微信小程序，等价于 !!window.wx
 * @see https://kms.fineres.com/pages/viewpage.action?pageId=1084298090
 * 为什么要排除PC端微信小程序？因为该环境下不加载SDK
 * @see FXD-43994
 */
export const isWeChatOrWxWorkUaWithoutPcMiniProgram = () => {
    return isWeChatOrWxWorkUA() && !isPcMiniProgramUA();
};

/**
 * 是否为微信或企业微信
 */
export const isWeChatOrWxWorkUA = () => {
    return /MicroMessenger/i.test(window.navigator.userAgent);
};

/**
 * 是否为PC微信小程序
 */
export const isPcMiniProgramUA = () => {
    return isMiniProgramUA() && !getMobileUA();
};

/**
 * 是否是企业微信的ua
 *
 * @returns {boolean}
 */
export const isWxWorkUA = () => {
    return /wxwork/i.test(window.navigator.userAgent);
};

/**
 * 当前环境是否为微信
 *
 * @returns {boolean}
 */
export const isWeChatUA = () => {
    return isWeChatOrWxWorkUA() && !isWxWorkUA();
};

/**
 * 判断是否为微信小程序
 */
export const isMiniProgramUA = (): boolean => {
    return /miniProgram/i.test(window.navigator.userAgent);
};

/**
 * 判断是否为移动端的微信小程序
 */
export const isMobileMiniProgramUA = (): boolean => {
    return (
        isMiniProgramUA() &&
        !(
            /MiniProgramEnv\/Windows/i.test(window.navigator.userAgent) ||
            /MiniProgramEnv\/Mac/i.test(window.navigator.userAgent)
        )
    );
};

/**
 * 判断当前浏览器环境是否为钉钉，并不一定是集成环境
 */
export const isDingTalkUA = () => {
    return /DingTalk/i.test(window.navigator.userAgent);
};

/**
 * 判断是否为app(接入SDK的app)
 */
export const isFXAppUA = () => {
    return /FXApp/i.test(window.navigator.userAgent);
};

export const isAppUA = () => {
    return isFXAppUA();
};

/**
 * 是否为新版 App 的 WebView (Web 会话模式, UA 末尾追加 app-web-session).
 * 新版 WebView 在服务端按手机浏览器处理, 会话失效后走 Web 自动恢复;
 * isAppUA 新旧版都为真, 只对新版生效的行为用本方法判定.
 */
export const isAppWebSessionUA = () => {
    return /app-web-session/i.test(window.navigator.userAgent);
};

/**
 * 新版 App WebView 的会话失效处理: 刷新当前页一次, 由服务端走 Web 自动恢复
 * (恢复不了时服务端跳到结果页 r=signed_out, App 退出).
 * 同一时刻多个请求失败只刷新一次; 标记挂在 window 上, 跨 SDK 与应用的独立打包同样去重.
 */
export const reloadPageOnceForWebSession = () => {
    const win = window as unknown as { __fxWebSessionReloading?: boolean };
    if (win.__fxWebSessionReloading) {
        return;
    }
    win.__fxWebSessionReloading = true;
    window.location.reload();
};

/**
 * 是否为国内App
 */
export const isJdyAppUA = () => {
    return isFXAppUA() && /Jdy/.test(window.navigator.userAgent);
};

/**
 * 是否为国外 Jodoo App
 */
export const isJodooAppUA = () => {
    return isFXAppUA() && /Jodoo/.test(window.navigator.userAgent);
};

/**
 * 是否是钉钉PC客户端的ua
 */
export const isDingTalkPcUA = () => {
    const ua = window.navigator.userAgent;
    return /nw/.test(ua) && isDingTalkUA() && !getMobileUA();
};

/**
 * 判断浏览器环境是否为飞书
 */
export const isLarkUA = () => {
    return /Lark/i.test(window.navigator.userAgent);
};

/**
 * ios safari检测
 *
 * @see https://stackoverflow.com/questions/3007480/determine-if-user-navigated-from-mobile-safari/29696509#29696509
 */
export const isSafariUA = () => {
    const ua = window.navigator.userAgent;
    const iOS = /iP(ad|hone)/i.test(ua);
    const webkit = /WebKit/i.test(ua);
    return iOS && webkit && !/CriOS/i.test(ua);
};

/**
 * 是否为Mac平台
 */
export const isMacPlatform = () => {
    return /Mac|iPad/.test(window.navigator.platform);
};

/**
 * 是否为鸿蒙系统
 * @see https://developer.huawei.com/consumer/cn/doc/harmonyos-guides/web-default-useragent
 * @see https://open.feishu.cn/document/web-app/gadget-api/harmonyos-adaptation-guide#8306c723
 */
export const isHarmonyUA = () => {
    return /OpenHarmony/i.test(window.navigator.userAgent);
};

/**
 * 获取移动端浏览器UA
 */
export const getMobileUA = () => {
    const u = window.navigator.userAgent;
    if (u.indexOf('Android') > -1 || u.indexOf('Linux') > -1) {
        return UA.ANDROID;
    } else if (u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)) {
        return UA.IOS;
    } else if (isHarmonyUA() && /Tablet|Phone/.test(u)) {
        // 鸿蒙还有PC端，所以这里拿 /Tablet|Phone/ 再判断一下
        return UA.HARMONY;
    }
};

/**
 * 是否为集成环境（微信、企微、钉钉、飞书）
 * 注意，该工具方法仅通过UA来判断客户端环境，不建议用于判断企业类型
 * 如需判断企业类型，请使用 corpUserStore.isIntegratedCorpType
 */
export const isIntegratedUA = () => {
    return isWeChatOrWxWorkUA() || isDingTalkUA() || isLarkUA();
};

/**
 * 是否为移动端集成环境（微信、企微、钉钉、飞书）
 */
export const isMobileIntegratedUA = () => {
    return !!getMobileUA() && isIntegratedUA();
};

/**
 * 火狐浏览器
 */
export const isFireFoxUA = () => {
    return navigator && navigator.userAgent.match(/Firefox\/([\d.]+)/);
};
